// Cell-key resolver: maps (URL step, subscription state) to a markdown
// filename under content/subscribe/. Pure function for testability.
//
// Phase 6 will read intendedAmount from state.current.yaml.data to
// disambiguate the 02-kyc-submitted collision (KYC-just-submitted vs
// Allocate-just-submitted-with-intent). For 2.4b the Allocate widget
// owns its own pending state via React local state.

// Terminal phases (09-refunded, 10-kyc-rejected, 13-kyc-officer-rejected)
// are NOT handled here — Subscribe.tsx (Task 11) routes them away from
// the cell-pipeline before this resolver is called. If you reach this
// resolver with a terminal phase, you'll get incorrect cell-keys
// (e.g., 10-kyc-rejected → 'kyc.verified'). Phase 6 may add an
// explicit terminal-phase guard here once the routing is wired.

import type { SubscriptionState } from './investor';

export type SubscribeStepKey = 'kyc' | 'allocate' | 'sign' | 'fund' | 'mint';

export function resolveSubscribeCellKey(
  step: SubscribeStepKey,
  subscription: SubscriptionState | null,
): string {
  const phase = subscription?.currentPhase ?? '01-discovered';

  switch (step) {
    case 'kyc':
      if (phase.startsWith('01-')) return 'kyc.pre';
      if (phase.startsWith('02-kyc-submitted')) return 'kyc.submitted';
      return 'kyc.verified';

    case 'allocate':
      if (phase.startsWith('12-')) return 'allocate.attested';
      if (phase.startsWith('03-kyc-verified')) return 'allocate.pre';
      if (
        phase.startsWith('04-') ||
        phase.startsWith('05-') ||
        phase.startsWith('06-') ||
        phase.startsWith('07-') ||
        phase.startsWith('08-')
      ) {
        return 'allocate.attested';
      }
      return 'allocate.pre';

    case 'sign':
      if (
        phase.startsWith('05-') ||
        phase.startsWith('06-') ||
        phase.startsWith('07-') ||
        phase.startsWith('08-')
      ) {
        return 'sign.done';
      }
      if (phase.startsWith('12-') || phase.startsWith('04-')) return 'sign.ready';
      return 'sign.pending';

    case 'fund':
      if (phase.startsWith('06-') || phase.startsWith('07-') || phase.startsWith('08-')) {
        return 'fund.done';
      }
      if (phase.startsWith('05-')) return 'fund.ready';
      return 'fund.locked';

    case 'mint':
      if (
        !phase.startsWith('06-') &&
        !phase.startsWith('07-') &&
        !phase.startsWith('08-')
      ) {
        return 'mint.locked';
      }
      return isJustMinted(subscription?.raw?.updated_at) ? 'mint.just' : 'mint.holding';
  }
}

function isJustMinted(updatedAt: string | undefined): boolean {
  if (!updatedAt) return true;
  const t = new Date(updatedAt).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t < 60 * 60 * 1000; // 1h
}
